import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useData } from '../../contexts/DataContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShield, FiArrowLeft, FiCheckCircle, FiInfo, FiLoader } = FiIcons;

const questions = [
  {
    id: 'q1_financial_situation',
    text: 'What is your current overall financial situation',
    options: [
      'Little savings and a fair amount of debt',
      'Some savings and some debt',
      'Significant savings and little or no debt'
    ]
  },
  {
    id: 'q2_past_savings_trend',
    text: 'Over the last 24 months, has the amount that you are able to save every month',
    options: [
      'Decreased',
      'Not changed',
      'Increased'
    ]
  },
  {
    id: 'q3_investment_experience',
    text: 'When it comes to investing in stocks, bonds, mutual funds or ETFs would you describe yourself as',
    options: [
      'Inexperienced',
      'Somewhat experienced',
      'Experienced'
    ]
  },
  {
    id: 'q4_reaction_to_loss',
    text: 'How would you react if an investment you have invested in lost 20% of its value in the first year',
    options: [
      'Extremely concerned; sell my investment',
      'Concerned; consider selling my investment',
      'Not overly concerned; I’m in it for the long term'
    ]
  },
  {
    id: 'q5_alternative_income',
    text: 'In addition to your primary source of income, do you have alternative sources of income',
    options: [
      'No',
      'Yes; but the income is not stable',
      'Yes; and the income is stable'
    ]
  },
  {
    id: 'q6_expected_savings_trend',
    text: 'Over the next 24 months, do you expect the amount that you are able to save every month to',
    options: [
      'Decrease',
      'Not change',
      'Increase'
    ]
  },
  {
    id: 'q7_risk_attitude',
    text: 'In making financial and investment decisions you are',
    options: [
      'Conservative and try to minimize risk and avoid the possibility of any loss',
      'Willing to accept moderate losses to achieve potentially higher returns',
      'Willing to tolerate large losses for the potential of achieving higher returns'
    ]
  }
];

const RiskProfileSurvey = () => {
  const { riskProfile, updateRiskProfile } = useData();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showResult, setShowResult] = useState(false);

  useEffect(() => {
    if (riskProfile) {
      const initialAnswers = {};
      questions.forEach(q => {
        if (riskProfile[q.id]) initialAnswers[q.id] = riskProfile[q.id];
      });
      setAnswers(initialAnswers);
    }
  }, [riskProfile]);

  const handleSelect = (questionId, option) => {
    setAnswers(prev => ({ ...prev, [questionId]: option }));
    if (currentStep < questions.length - 1) {
      setTimeout(() => setCurrentStep(prev => prev + 1), 300);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await updateRiskProfile(answers);
      setShowResult(true);
    } catch (err) {
      console.error('Failed to save risk profile:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentQuestion = questions[currentStep];
  const progress = ((currentStep + 1) / questions.length) * 100;
  const isComplete = Object.keys(answers).length === questions.length;

  if (showResult && riskProfile) {
    return (
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-secondary-900 rounded-2xl border border-primary-500/20 p-12 text-center"
      >
        <div className="w-20 h-20 bg-primary-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <SafeIcon icon={FiCheckCircle} className="w-10 h-10 text-primary-500" />
        </div>
        <h2 className="text-3xl font-bold text-white mb-2">Profile Calculated</h2>
        <p className="text-secondary-400 mb-8">Your risk profile has been updated according to the model.</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto mb-10">
          <div className="bg-black p-6 rounded-2xl border border-primary-500/10">
            <p className="text-xs font-black text-secondary-500 uppercase tracking-widest mb-2">Risk Rating Score</p>
            <p className="text-4xl font-black text-primary-500">{riskProfile.client_risk_rating_score}%</p>
          </div>
          <div className="bg-black p-6 rounded-2xl border border-primary-500/10">
            <p className="text-xs font-black text-secondary-500 uppercase tracking-widest mb-2">Risk Profile</p>
            <p className="text-2xl font-bold text-white uppercase tracking-tight">{riskProfile.risk_profile}</p>
          </div>
        </div>

        <button 
          onClick={() => setShowResult(false)}
          className="text-primary-500 hover:text-primary-400 font-bold flex items-center justify-center mx-auto"
        >
          <SafeIcon icon={FiArrowLeft} className="mr-2" />
          Review Answers
        </button>
      </motion.div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <div className="flex justify-between items-end mb-4">
          <div>
            <h2 className="text-2xl font-bold text-white">Client Risk Profile</h2>
            <p className="text-secondary-400">Step {currentStep + 1} of {questions.length}</p>
          </div>
          <span className="text-primary-500 font-bold">{Math.round(progress)}%</span>
        </div>
        <div className="h-2 w-full bg-secondary-800 rounded-full overflow-hidden">
          <motion.div 
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            className="h-full bg-primary-500 shadow-[0_0_10px_rgba(240,160,48,0.5)]"
          />
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="bg-secondary-900 rounded-2xl border border-primary-500/20 p-8 md:p-12 shadow-2xl"
        >
          <h3 className="text-xl md:text-2xl font-bold text-white mb-8 leading-tight">
            “{currentQuestion.text}”
          </h3>

          <div className="space-y-4">
            {currentQuestion.options.map((option, idx) => (
              <button
                key={idx}
                onClick={() => handleSelect(currentQuestion.id, option)}
                className={`w-full text-left p-6 rounded-xl border transition-all flex items-center justify-between group ${
                  answers[currentQuestion.id] === option
                    ? 'bg-primary-500 border-primary-500 text-black'
                    : 'bg-black/40 border-secondary-800 text-secondary-300 hover:border-primary-500/50 hover:bg-primary-500/5'
                }`}
              >
                <span className="font-medium">{option}</span>
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                  answers[currentQuestion.id] === option ? 'border-black' : 'border-secondary-700 group-hover:border-primary-500'
                }`}>
                  {answers[currentQuestion.id] === option && <div className="w-3 h-3 bg-black rounded-full" />}
                </div>
              </button>
            ))}
          </div>

          <div className="flex justify-between items-center mt-12 pt-8 border-t border-secondary-800">
            <button
              onClick={handleBack}
              disabled={currentStep === 0}
              className="flex items-center space-x-2 text-secondary-500 hover:text-white disabled:opacity-0 transition-colors font-bold"
            >
              <SafeIcon icon={FiArrowLeft} />
              <span>Back</span>
            </button>

            {isComplete && currentStep === questions.length - 1 ? (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="bg-primary-500 hover:bg-primary-400 text-black px-10 py-4 rounded-xl font-black flex items-center space-x-3 transition-all shadow-lg shadow-primary-500/20 disabled:opacity-50"
              >
                {isSubmitting ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiShield} />}
                <span>Calculate Risk Score</span>
              </button>
            ) : null}
          </div>
        </motion.div>
      </AnimatePresence>

      <div className="mt-8 bg-black/40 border border-secondary-800 p-6 rounded-2xl flex items-start space-x-4">
        <SafeIcon icon={FiInfo} className="w-6 h-6 text-primary-500 flex-shrink-0 mt-1" />
        <p className="text-sm text-secondary-400 leading-relaxed">
          Your answers are weighted according to the Ashdale Risk Model to ensure your retirement and investment projections remain aligned with your volatility tolerance.
        </p>
      </div>
    </div>
  );
};

export default RiskProfileSurvey;